import { Stack, Box, Switch, Divider, FormControlLabel } from '@vapor/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BillF, DepositF, ReservationF } from '../../../types/reservation-detail.types'
import { reduceAssignedProductions } from '../../../utils/bills'
import ProductionsAssignedGrid from './productions-assigned-grid/productions-assigned-grid'
import BillsActions from './bills-actions/bills-actions'
import CheckoutDocumentForm, { CreateCoParameter, UpdateCoParameter } from 'src/app/pages/accounting/documents/checkout-document/checkout-document-form'
import useCheckoutApi from '../../reserved-resources-tab/check-out/check-out.api'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { useConfirm } from 'hic-components'
import PosDrawer from '../../../../pos/pos-drawer'
import { ProductionAssignedActionType } from './productions-assigned-actions/production-assigned-actions.type'
import { ProductionAssignedActionsDialog } from './productions-assigned-actions/production-assigned-actions-dialog'
import { useUpdateProductionMutation } from 'src/api/api.generated'
import { ProductionAssignedRow } from './productions-assigned-grid/productions-assigned-grid.types'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { ProductionAssignedViewOptions } from './productions-assigned-grid/production-assigned-view-options'
import DocumentReportViewer from 'src/app/components/report-viewer/documnent/document-report-viewer'
import { ProductionsCheck } from './productions/procuctions-check'

export type BillsProps = {
  readOnly: boolean
  propertyId: string
  reservation: ReservationF
  reservedResourceId: string | null
  bills: BillF[]
  deposits: DepositF[]
  selectedDepositIds: string[]
}

export default function Bills({ readOnly, propertyId, reservation, reservedResourceId, bills, deposits, selectedDepositIds }: BillsProps) {
  const { t } = useTranslation()
  const hotelDate = useHotelDate()
  const confirm = useConfirm()
  const showError = useErrorSnackBar()

  const { validateCheckOut, checkOut, areThereAnyOpenBills } = useCheckoutApi()
  const [updateData, { loading: updatePending }] = useUpdateProductionMutation()

  // Are the reserved resouce IDs that belong to this reservation
  const rrIdsOfCurrentReservation = useMemo(() => {
    return reservation.reservedResources?.map<string>((rr) => rr.id)
  }, [reservation])

  const assignedProductions = useMemo(() => {
    const filteredBills = !reservedResourceId
      ? bills
      : bills.filter((x) => {
          return (
            x.reservedResourceId === null ||
            x.reservedResourceId === undefined ||
            x.reservedResourceId.includes(reservedResourceId) ||
            x.productions?.some((p) => {
              const res = p.production?.reservedResource?.id && !rrIdsOfCurrentReservation?.includes(p.production?.reservedResource?.id)
              return res
            })
          )
        })

    return reduceAssignedProductions(filteredBills)
  }, [JSON.stringify(bills), reservedResourceId, rrIdsOfCurrentReservation])
  // const [filteredAssignedProductions, setFilteredAssignedProductions] = useState<AssignedProductionF[]>([])

  const [selectedAssignedProductionIds, setSelectedAssignedProductionIds] = useState<string[]>([])

  const [printDocumentId, setPrintDocumentId] = useState<string | null>(null)
  const [checkoutDocumentFormParams, setCheckoutDocumentFormParams] = useState<CreateCoParameter | UpdateCoParameter | null>(null)
  const [requestCheckout, setRequestCheckout] = useState<boolean>(false)

  const reservedResourcesIds = useMemo(() => {
    return reservation.reservedResources?.map((rr) => rr.id) ?? []
  }, [reservation.reservedResources])

  const allTheDocumentsAreClosed = useMemo(() => areThereAnyOpenBills(reservation, bills, reservedResourcesIds).length === 0, [bills, reservation, reservedResourcesIds, areThereAnyOpenBills])

  const onSuccessCheckoutDocument = useCallback(
    (closedBillsIds?: string[], noAskCheckoutConfirm?: boolean) => {
      setSelectedAssignedProductionIds([])
      // Aggiorno lo stato dei conti chiusi dal documeto.
      const closedBills = bills.map((b) => (closedBillsIds?.includes(b.id) ? { ...b, open: false } : { ...b }))

      // Controlliamo se tutti i conti della prenotazione sono chiusi
      if (validateCheckOut(reservation, closedBills, deposits, reservedResourcesIds).nextAction === 'CHECKOUT') {
        // Verificare se la prenotazion è nelle condizioni di check-out
        if (reservation.maxEnd && reservation.maxEnd <= hotelDate) {
          if (noAskCheckoutConfirm) {
            checkOut(reservation.tenantId, reservation.propertyId[0], reservation.id, reservedResourcesIds)
            setRequestCheckout(false)
          } else {
            confirm({
              title: t('pms.booking.reservation.check_out_confirm_title'),
              message: t('pms.booking.reservation.check_out_confirm_description'),
              confirmButtonText: t('commons.yes'),
              cancelButtonText: t('commons.no'),
              onConfirm: () => {
                checkOut(reservation.tenantId, reservation.propertyId[0], reservation.id, reservedResourcesIds)
              },
            })
          }
        }
      }
    },
    [reservation, bills, deposits, reservedResourcesIds, validateCheckOut, checkOut, confirm],
  )

  const [posBill, setPosBill] = useState<string>()
  const onAddNewProductionAction = (billId: string) => {
    setPosBill(billId)
  }

  const [assignedProductionIdAction, setAssignedProductionIdAction] = useState<{ ids: string[]; action: ProductionAssignedActionType }>()

  const [rowChildrenExpanded, setRowChildrenExpanded] = useState<boolean>(localStorage.getItem('expandedBills') !== 'false')
  const [groupedCityTaxView, setGroupedByCityTaxView] = useState<boolean>(localStorage.getItem('groupedCityTaxView') !== 'false')
  const [groupedStayView, setGroupedByStayView] = useState<boolean>(localStorage.getItem('groupedStayView') !== 'false')
  const [groupedExtraView, setGroupedByExtraView] = useState<boolean>(localStorage.getItem('groupedExtraView') !== 'false')

  const handleUpdateProduction = useCallback(
    async (productionId: string, row: ProductionAssignedRow, vatId: string, chargeCategoryId: string, onSuccess: () => void) => {
      try {
        const result = await updateData({
          variables: {
            id: productionId,
            value: {
              tenantId: reservation.tenantId,
              propertyId: propertyId,
              date: row.date,
              reservedResourceId: row.reservedResourceId,
              chargeCategoryId: chargeCategoryId,
              currency: row.currency,
              description: row.description ? row.description : null,
              quantity: row.quantity,
              amount: row.amount,
              total: row.total,
              vatId: vatId,
            },
          },
        })
        if (result.data && !result.errors) {
          onSuccess()
        } else if (result.errors) showError(result.errors)
      } catch (error) {
        showError(error)
      }
    },
    [updateData, reservation.tenantId, propertyId, assignedProductions],
  )

  //  #region grouped bill grid force rendering
  // Necessary to force grid re-rendering and produce a calculation of groups state (not calculable after grid rendering).
  // When a new document is created, the relative bill's group will be  collapsed.
  const openedBills = useMemo(() => {
    const opened = bills.filter((b) => b.open).length ?? 0
    return opened
  }, [bills])

  const [gridKey, setGridKey] = useState(0)

  useEffect(() => {
    let currentV = 0
    setGridKey((v) => {
      currentV = v + 1
      return currentV
    })
  }, [openedBills])
  //#endregion

  return (
    <>
      <Stack direction="column" spacing={2}>
        {/* <Box display={'none'}>
          <Filter // INFO: AL MOMENTO QUESTA FUNZIONALITA' E' STATA DISATTIVATA.
            bills={bills}
            assignedProductions={assignedProductions}
            onFilteredAssignedProdcutionsChange={setFilteredAssignedProductions}
          />
        </Box> */}
        <BillsActions //
          {...{ bills, deposits, reservation }}
          propertyId={propertyId}
          selectedAssignedProductionIds={selectedAssignedProductionIds}
          onCreateDocument={(params) => {
            setCheckoutDocumentFormParams(params)
            setRequestCheckout(true)
          }}
          readOnly={readOnly}
          allDocumentsAreClosed={allTheDocumentsAreClosed}
          selectedDepositIds={selectedDepositIds}
        />
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <ProductionAssignedViewOptions
            rowChildrenExpanded={rowChildrenExpanded}
            setRowChildrenExpanded={(expanded) => {
              setRowChildrenExpanded(expanded)
              localStorage.setItem('expandedBills', expanded.toString())
            }}
            groupedCityTaxView={groupedCityTaxView}
            setGroupedCityTaxView={(enabled) => {
              setGroupedByCityTaxView(enabled)
              localStorage.setItem('groupedCityTaxView', enabled.toString())
            }}
            groupedStayView={groupedStayView}
            setGroupedStayView={(enabled) => {
              setGroupedByStayView(enabled)
              localStorage.setItem('groupedStayView', enabled.toString())
            }}
            groupedExtraView={groupedExtraView}
            setGroupedExtraView={(enabled) => {
              setGroupedByExtraView(enabled)
              localStorage.setItem('groupedExtraView', enabled.toString())
            }}
          />
          <ProductionsCheck propertyId={propertyId} reservedResourceId={reservedResourceId || undefined} reservationId={reservation.id} assignedProductions={assignedProductions} />
        </Stack>
        <ProductionsAssignedGrid //
          propertyId={propertyId}
          bills={bills}
          key={gridKey}
          assignedProductions={assignedProductions}
          reservedResourceId={reservedResourceId}
          onAssignedProductionsSelectedChange={setSelectedAssignedProductionIds}
          onAssignedProductionAction={(ids, action) => setAssignedProductionIdAction({ action: action, ids: ids })}
          onOpenDocument={(params) => setCheckoutDocumentFormParams(params)}
          readOnly={reservation.reservationState.canceled || readOnly}
          onUpdateProduction={handleUpdateProduction}
          onAddNewProduction={onAddNewProductionAction}
          groupedCityTaxView={groupedCityTaxView}
          groupedStayView={groupedStayView}
          groupedExtraView={groupedExtraView}
          allDocumentsAreClosed={allTheDocumentsAreClosed}
          loading={updatePending}
          rowChildrenExpanded={rowChildrenExpanded}
          rrIdsOfCurrentReservation={rrIdsOfCurrentReservation}
          reservation={reservation}
        />
        <Divider light />
      </Stack>
      <PosDrawer propertyId={propertyId} billId={posBill} closeDrawer={() => setPosBill(undefined)} />
      {checkoutDocumentFormParams !== null && (
        <CheckoutDocumentForm //
          propertyId={propertyId}
          open={checkoutDocumentFormParams != null}
          onClose={() => {
            setCheckoutDocumentFormParams(null)
            setRequestCheckout(false)
          }}
          onSuccess={({ closedBillsIds, documentId }) => {
            setPrintDocumentId(documentId)
            onSuccessCheckoutDocument(closedBillsIds, requestCheckout)
          }}
          refetchQueries={['ReservationDetail']}
          onCreditNoteSuccess={() => {
            setCheckoutDocumentFormParams(null)
            setRequestCheckout(false)
          }}
          {...checkoutDocumentFormParams}
        />
      )}
      {printDocumentId && (
        <DocumentReportViewer
          open={printDocumentId !== null}
          setClose={() => {
            setPrintDocumentId(null)
          }}
          documentId={printDocumentId}
          propertyId={propertyId}
        />
      )}
      {assignedProductionIdAction && (
        <ProductionAssignedActionsDialog //
          action={assignedProductionIdAction.action}
          productionAssignedIds={assignedProductionIdAction.ids}
          bills={bills.filter((bill) => bill.open)}
          open={assignedProductionIdAction ? true : false}
          onClose={() => setAssignedProductionIdAction(undefined)}
          reservation={reservation}
        />
      )}
    </>
  )
}
